import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import {
  BPTestingProjectResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { BPTestingProjectStatus, SortDirection } from '@/utils/enums';

import BPTestingProjectTableRow from './BPTestingProjectTableRow';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: 'created_at',
};

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    vertical-align: top;
    &:nth-child(1) {
      padding-left: 24px;
      width: calc(36% - 56px);
      max-width: 128px;
    }
    &:nth-child(2) {
      width: 16%;
    }
    &:nth-child(3) {
      width: 16%;
    }
    &:nth-child(4) {
      width: 18%;
    }
    &:nth-child(5) {
      width: 14%;
      padding-right: 36px;
    }
    &:last-child {
      min-width: 48px;
      width: 48px;
      z-index: 1;
      position: relative;
      border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
    }
  }
  th:last-child {
    border-left: 0 !important;
  }
`;

const FiltersHolder = styled.div`
  display: flex;
`;

const StyledSearchBar = styled(SearchBar)`
  margin-bottom: 12px;
`;

interface Props {
  status: BPTestingProjectStatus;
}

const BPTestingProjectsTable: FC<Props> = ({ status }) => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const fetchPosts = ({ pageParam = '/v1/editorial/bp-testing-projects' }) => {
    return api.get<never, PaginatedResponse<BPTestingProjectResource>>(
      pageParam,
      {
        params: {
          'filter[status]': status,
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
          ...(!!debouncedSearch && {
            'filter[search]': debouncedSearch,
          }),
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'bp_testing_projects',
      {
        status,
        sort,
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    fetchPosts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 90 * 1000,
    },
  );

  const projects = useMemo(
    () =>
      data?.pages?.reduce<BPTestingProjectResource[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  return (
    <>
      <TableHeader>
        <Controls>
          <ControlsButton
            active={status === BPTestingProjectStatus.ACTIVE}
            as={Link}
            to={`/general/bp-testing-projects`}
          >
            Active
          </ControlsButton>
          <ControlsButton
            active={status === BPTestingProjectStatus.ENDED}
            as={Link}
            to={`/general/bp-testing-projects/ended`}
          >
            Ended
          </ControlsButton>
        </Controls>
        <FiltersHolder>
          <FullScreenButton />
        </FiltersHolder>
      </TableHeader>
      <StyledSearchBar
        setValue={setSearch}
        value={search}
        placeholder="Search by Project Name"
      />
      <ResponsiveTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Project Name</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="created_at"
                title="Created At"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="owner"
                title="Owner"
              />
              <Th>Status</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!projects?.length}
            >
              {projects?.map((project) => (
                <BPTestingProjectTableRow key={project.id} project={project} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default BPTestingProjectsTable;
